// src/components/fraud/BlacklistTable.js
// Paginated table for the Blacklist Management page.
// Schema: { uid, email, phone, ip, reason, notes, active, createdAt, createdBy }
// A record may carry multiple identifiers — all are shown as stacked badges.
// Columns: Identifiers | Reason | Created By | Created At | Status | Actions

import React, { useState, useRef, useEffect } from 'react';
import { Table } from 'react-bootstrap';
import { BL_PAGE_SIZE, IDENTIFIER_FIELDS } from '../../hooks/useBlacklist';

// ─── Identifier badges cell ───────────────────────────────────────────────────
// Shows ALL populated identifier fields for a record as stacked rows.
const IdentifiersCell = ({ entry }) => {
  const populated = IDENTIFIER_FIELDS.filter((f) => entry[f.key]);
  if (populated.length === 0)
    return <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>—</span>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {populated.map((f) => (
        <IdentifierRow key={f.key} field={f} value={entry[f.key]} />
      ))}
    </div>
  );
};

// One identifier row: [TYPE BADGE] [shortened value + copy]
const IdentifierRow = ({ field, value }) => {
  const [copied, setCopied] = useState(false);
  const short = value.length > 20
    ? `${value.substring(0, 10)}…${value.substring(value.length - 5)}`
    : value;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      {/* Type badge */}
      <span
        style={{
          fontSize: 9,
          fontWeight: 700,
          color: field.color,
          background: `${field.color}18`,
          border: `1px solid ${field.color}44`,
          borderRadius: 3,
          padding: '1px 5px',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          flexShrink: 0,
        }}
      >
        {field.label}
      </span>
      {/* Value */}
      <span
        title={value}
        style={{
          fontSize: 11,
          fontFamily: 'monospace',
          color: 'var(--text-primary)',
          cursor: 'default',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 160,
          display: 'inline-block',
        }}
      >
        {short}
      </span>
      {/* Copy button */}
      <button
        title={copied ? 'Copied!' : `Copy ${field.label}`}
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(value).then(() => {
            setCopied(true); setTimeout(() => setCopied(false), 1800);
          });
        }}
        style={{
          background: 'transparent', border: 'none', padding: '0 1px',
          cursor: 'pointer', color: copied ? '#22C55E' : 'var(--text-secondary)',
          fontSize: 11, lineHeight: 1, flexShrink: 0,
        }}
      >
        {copied ? '✓' : '⧉'}
      </button>
    </div>
  );
};

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ active }) => (
  <span
    style={{
      fontSize: 10, fontWeight: 700,
      color: active ? '#22C55E' : '#A6A3B8',
      background: active ? 'rgba(34,197,94,0.12)' : 'rgba(166,163,184,0.10)',
      border: `1px solid ${active ? 'rgba(34,197,94,0.3)' : 'rgba(166,163,184,0.2)'}`,
      borderRadius: 20, padding: '2px 8px', whiteSpace: 'nowrap',
    }}
  >
    {active ? 'Active' : 'Disabled'}
  </span>
);

// ─── Actions dropdown ─────────────────────────────────────────────────────────
const ActionsMenu = ({ entry, onView, onEdit, onToggle, onDelete, opLoading }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const menuItem = (label, onClick, color) => (
    <button
      onClick={(e) => { e.stopPropagation(); setOpen(false); onClick(); }}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        background: 'transparent', border: 'none',
        padding: '7px 14px', cursor: 'pointer',
        fontSize: 12, color: color || 'var(--text-primary)',
        transition: 'background 0.1s',
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      {label}
    </button>
  );

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={opLoading}
        title="Row actions"
        style={{
          background: open ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.10)', borderRadius: 6,
          color: 'var(--text-primary)', width: 28, height: 28,
          cursor: 'pointer', fontSize: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        ⋮
      </button>
      {open && (
        <div
          style={{
            position: 'absolute', right: 0, top: 32, zIndex: 900,
            background: 'var(--bg-surface)', border: '1px solid var(--divider)',
            borderRadius: 8, minWidth: 130,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)', overflow: 'hidden',
          }}
        >
          {menuItem('👁 View',   onView)}
          {menuItem('✏️ Edit',   onEdit)}
          {menuItem(entry.active ? '⊘ Disable' : '✓ Enable', onToggle, entry.active ? '#F59E0B' : '#22C55E')}
          {menuItem('🗑 Delete', onDelete, '#EF4444')}
        </div>
      )}
    </div>
  );
};

// ─── Skeleton rows ────────────────────────────────────────────────────────────
const SkeletonRows = () =>
  Array.from({ length: 6 }).map((_, i) => (
    <tr key={i}>
      {[180, 140, 100, 80, 60, 40].map((w, j) => (
        <td key={j} className="transparent-header py-3">
          <div className="fraud-skeleton" style={{ width: w, height: 12, borderRadius: 4 }} />
        </td>
      ))}
    </tr>
  ));

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyState = ({ hasFilters }) => (
  <tr>
    <td colSpan={6} className="transparent-header text-center py-5">
      <div style={{ color: 'var(--text-secondary)' }}>
        <div style={{ fontSize: 36, marginBottom: 10 }}>{hasFilters ? '🔍' : '🚫'}</div>
        <p style={{ fontSize: 14, marginBottom: 4 }}>
          {hasFilters ? 'No entries match your filters' : 'Blacklist is empty'}
        </p>
        <small style={{ fontSize: 12 }}>
          {hasFilters ? 'Try adjusting or clearing the filters' : 'Add an entry using the button above'}
        </small>
      </div>
    </td>
  </tr>
);

// ─── Pagination ───────────────────────────────────────────────────────────────
const Pagination = ({ page, totalPages, hasPrev, hasNext, onPrev, onNext, total, loading }) => (
  <div className="d-flex justify-content-between align-items-center mt-3 px-1" style={{ flexWrap: 'wrap', gap: 8 }}>
    <small style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
      Page {page} of {totalPages} · {total} {total === 1 ? 'entry' : 'entries'} · {BL_PAGE_SIZE} per page
    </small>
    <div className="d-flex gap-2">
      <button
        id="bl-prev-btn" onClick={onPrev} disabled={!hasPrev || loading}
        style={{
          background: !hasPrev ? 'rgba(255,255,255,0.04)' : 'var(--bg-surface)',
          border: '1px solid var(--divider)', borderRadius: 6,
          color: !hasPrev ? 'var(--text-secondary)' : 'var(--text-primary)',
          padding: '5px 14px', cursor: !hasPrev ? 'default' : 'pointer',
          fontSize: 12, fontWeight: 600,
        }}
      >
        ← Prev
      </button>
      <button
        id="bl-next-btn" onClick={onNext} disabled={!hasNext || loading}
        style={{
          background: !hasNext ? 'rgba(255,255,255,0.04)' : 'var(--accent-lime)',
          border: 'none', borderRadius: 6,
          color: !hasNext ? 'var(--text-secondary)' : '#050014',
          padding: '5px 14px', cursor: !hasNext ? 'default' : 'pointer',
          fontSize: 12, fontWeight: 700,
        }}
      >
        Next →
      </button>
    </div>
  </div>
);

// ─── Format date ──────────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ─── Main component ───────────────────────────────────────────────────────────
const COLUMNS = ['Identifiers', 'Reason', 'Created By', 'Created At', 'Status', 'Actions'];

const BlacklistTable = ({
  entries, loading, opLoading, hasFilters,
  page, totalPages, hasNext, hasPrev, onNext, onPrev, total,
  onView, onEdit, onToggle, onDelete,
}) => {
  const [hovered, setHovered] = useState(null);

  return (
    <div id="blacklist-table" className="theme-card rounded-3 p-4">
      <div className="table-responsive">
        <Table className="align-middle body-text-two mb-0" style={{ minWidth: 720 }}>
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col}
                  className="transparent-header"
                  style={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', paddingBottom: 10 }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : entries.length === 0 ? (
              <EmptyState hasFilters={hasFilters} />
            ) : (
              entries.map((entry, idx) => (
                <tr
                  key={entry._id || idx}
                  onClick={() => onView(entry)}
                  onMouseEnter={() => setHovered(idx)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    borderBottom: '1px solid var(--divider)',
                    cursor: 'pointer',
                    background: hovered === idx ? 'rgba(217,255,31,0.04)' : 'transparent',
                    transition: 'background 0.15s',
                    verticalAlign: 'top',
                  }}
                >
                  {/* Identifiers — all populated uid/email/phone/ip */}
                  <td className="transparent-header py-3">
                    <IdentifiersCell entry={entry} />
                  </td>

                  {/* Reason */}
                  <td className="transparent-header py-3" style={{ fontSize: 12, maxWidth: 200 }}>
                    {entry.reason ? (
                      <span title={entry.reason} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                        {entry.reason}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-secondary)' }}>—</span>
                    )}
                  </td>

                  {/* Created By */}
                  <td className="transparent-header py-3" style={{ fontSize: 12 }}>
                    {entry.createdBy || <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                  </td>

                  {/* Created At */}
                  <td className="transparent-header py-3" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                    {fmtDate(entry.createdAt)}
                  </td>

                  {/* Status */}
                  <td className="transparent-header py-3">
                    <StatusBadge active={entry.active} />
                  </td>

                  {/* Actions */}
                  <td className="transparent-header py-3">
                    <ActionsMenu
                      entry={entry} opLoading={opLoading}
                      onView={() => onView(entry)}
                      onEdit={() => onEdit(entry)}
                      onToggle={() => onToggle(entry._id, entry.active)}
                      onDelete={() => onDelete(entry)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      <Pagination
        page={page} totalPages={totalPages}
        hasPrev={hasPrev} hasNext={hasNext}
        onPrev={onPrev} onNext={onNext}
        total={total} loading={loading}
      />
    </div>
  );
};

export default BlacklistTable;
